// The six day-vibe definitions plus applyVibe and smart defaults. A vibe is a
// preset patch over PlannerPreferences plus exactly one objective weight set to
// a tuned value. One-tap selection applies the whole patch; in fine-tune the
// objectives layer independently (each toggle just flips one weight). MTB swaps
// the two ski-centric vibes (First tracks, Chase the sun) for Flow day and Big
// descents, which still ride on the existing objective keys but also nudge the
// MTB riding/climbing axes. See design doc sections 3, 9, 10.

import {
  DEFAULT_MTB_PREFERENCES,
  DEFAULT_PREFERENCES,
  EMPTY_OBJECTIVES,
  type MtbPreferences,
  type PlannerObjectives,
  type PlannerPreferences,
  type SkiPreferences,
} from "./preferences";
import type { AreaSport } from "./graph-types";
import { applyDayStyle, type DayStyle } from "./day-styles";

// The objective weight a vibe switches on, and the value it sets. Tuned so a
// single objective bends the plan noticeably without overwhelming the raw score.
export type ObjectiveKey = keyof PlannerObjectives;

// A vibe key is the objective it primarily drives, except MTB renames the two
// ski-centric ones. "flow" and "bigDescents" map onto cruise/firstTracks
// objectives respectively but carry MTB-specific copy and preset patches.
export type SkiVibeKey = "sun" | "scenic" | "firstTracks" | "coverage" | "cruise" | "quiet";
export type MtbVibeKey = "flow" | "bigDescents" | "scenic" | "coverage" | "cruise" | "quiet";
export type VibeKey = SkiVibeKey | MtbVibeKey;

export type VibeDef = {
  key: VibeKey;
  label: string;
  icon: string;
  /** The objective weight this vibe switches on. */
  objective: ObjectiveKey;
  weight: number;
  /** Short, honest blurb for the card. No fees, no measured-queue claims. */
  blurb: string;
};

// --- Smart defaults ---------------------------------------------------------

/**
 * The starting point a one-tap vibe patches: a sensible "Cruiser, Partner, full
 * day" plan with all objectives off. The vibe then overrides the relevant
 * fields. Ski and MTB each return their own preference shape.
 */
export function smartDefaults(sport: AreaSport): PlannerPreferences {
  if (sport === "mtb") {
    return {
      ...DEFAULT_MTB_PREFERENCES,
      ability: "intermediate",
      difficultyMode: "comfort",
      distance: "full-day",
      group: "couple",
      lunch: { time: "12:30", style: "sit-down", price: "mid" },
      coffeeStops: "morning",
      objectives: { ...EMPTY_OBJECTIVES },
    };
  }
  return {
    ...DEFAULT_PREFERENCES,
    ability: "intermediate",
    difficultyMode: "comfort",
    distance: "full-day",
    group: "couple",
    lunch: { time: "12:30", style: "sit-down", price: "mid" },
    coffeeStops: "morning",
    liftPrefs: { avoidDrags: true, preferGondolas: false },
    objectives: { ...EMPTY_OBJECTIVES },
  };
}

// --- Catalogue --------------------------------------------------------------

const W = 0.6; // shared tuned objective weight; bends without dominating

export const VIBES: VibeDef[] = [
  { key: "sun", label: "Chase the sun", icon: "☀️", objective: "sun", weight: W,
    blurb: "Follows the sun across the mountain, east faces in the morning, west in the afternoon." },
  { key: "scenic", label: "Big views", icon: "🏔️", objective: "scenic", weight: W,
    blurb: "Favours high, open runs with the big panoramas." },
  { key: "firstTracks", label: "First tracks", icon: "🌅", objective: "firstTracks", weight: W,
    blurb: "An early start onto the best freshly groomed runs before the crowds." },
  { key: "coverage", label: "Cover ground", icon: "🧭", objective: "coverage", weight: W,
    blurb: "Sees as much of the mountain as a day allows, new sectors and runs throughout." },
  { key: "cruise", label: "Chill cruise", icon: "😎", objective: "cruise", weight: W,
    blurb: "Easy blue and green groomers and a long sit-down lunch." },
  { key: "quiet", label: "Quiet day", icon: "🤫", objective: "quiet", weight: W,
    blurb: "An estimated quieter route that steers around the busiest lifts at their busiest times." },
];

export const MTB_VIBES: VibeDef[] = [
  { key: "flow", label: "Flow day", icon: "🌊", objective: "cruise", weight: W,
    blurb: "Smooth, flowing trails that keep the wheels rolling." },
  { key: "bigDescents", label: "Big descents", icon: "⛰️", objective: "firstTracks", weight: W,
    blurb: "Maximises long descents, lift-served where it can be." },
  { key: "scenic", label: "Big views", icon: "🏔️", objective: "scenic", weight: W,
    blurb: "Favours high, open trails with the big panoramas." },
  { key: "coverage", label: "Cover ground", icon: "🧭", objective: "coverage", weight: W,
    blurb: "Rides as much of the trail network as a day allows." },
  { key: "cruise", label: "Chill ride", icon: "😎", objective: "cruise", weight: W,
    blurb: "Easy trails and a long sit-down lunch." },
  { key: "quiet", label: "Quiet day", icon: "🤫", objective: "quiet", weight: W,
    blurb: "An estimated quieter route around the busiest uplifts." },
];

export function vibesForSport(sport: AreaSport): VibeDef[] {
  return sport === "mtb" ? MTB_VIBES : VIBES;
}

// --- applyVibe --------------------------------------------------------------

// Bring the start ~45 min earlier than the current start, floored at 08:00.
function earlierStart(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const mins = Math.max(8 * 60, (h || 0) * 60 + (m || 0) - 45);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

function patchFirstTracks(prefs: SkiPreferences): SkiPreferences {
  return {
    ...prefs,
    startTime: earlierStart(prefs.startTime),
    lunch: { ...prefs.lunch, time: "13:00" },
  };
}

function patchSki(prefs: SkiPreferences, key: SkiVibeKey): SkiPreferences {
  const styleForKey: Record<SkiVibeKey, DayStyle> = {
    sun: "sun-and-views",
    scenic: "sun-and-views",
    firstTracks: "best-snow",
    coverage: "cover-ground",
    cruise: "easy-social",
    quiet: "quiet-day",
  };
  const styled = applyDayStyle(prefs, styleForKey[key]) as SkiPreferences;
  switch (key) {
    case "firstTracks":
      return patchFirstTracks(styled);
    case "sun":
    case "scenic":
    case "coverage":
    case "cruise":
    case "quiet":
      return styled;
    default:
      // Unknown or cross-sport key (e.g. an MTB key passed via a user-controlled
      // deep link): leave prefs untouched rather than returning undefined.
      return prefs;
  }
}

function patchMtb(prefs: MtbPreferences, key: MtbVibeKey): MtbPreferences {
  const styleForKey: Record<MtbVibeKey, DayStyle> = {
    flow: "flow-day",
    bigDescents: "big-descents",
    scenic: "locals-best-ride",
    coverage: "xc-adventure",
    cruise: "easy-social-ride",
    quiet: "quiet-uplift",
  };
  const styled = applyDayStyle(prefs, styleForKey[key]) as MtbPreferences;
  switch (key) {
    case "scenic":
      return { ...styled, objectives: { ...EMPTY_OBJECTIVES, scenic: W } };
    case "flow":
    case "bigDescents":
    case "coverage":
    case "cruise":
    case "quiet":
      return styled;
    default:
      // Unknown or cross-sport key: leave prefs untouched (see patchSki).
      return prefs;
  }
}

/** True when `key` is a real vibe for the given sport. Use to validate any
 * user-controlled value (a `?vibe=` deep link) before applying it. */
export function isValidVibe(sport: AreaSport, key: string): key is VibeKey {
  return vibesForSport(sport).some((v) => v.key === key);
}

/**
 * Produce a PlannerPreferences value for a vibe tap: the preset patch plus the
 * one objective switched on. The sport of `prefs` selects the arm. A key that is
 * not valid for that sport (only possible via an unvalidated deep link) leaves
 * prefs unchanged rather than corrupting them, so the return is never undefined.
 */
export function applyVibe(prefs: PlannerPreferences, key: VibeKey): PlannerPreferences {
  if (prefs.sport === "mtb") return patchMtb(prefs, key as MtbVibeKey);
  return patchSki(prefs, key as SkiVibeKey);
}
